import React, { useEffect, useState, useMemo } from "react";
import {
  Select, Button, Modal, Form, Switch, Input,
  Tooltip, message, Empty, Spin, Tag,
} from "antd";
import {
  PlusOutlined, SaveOutlined, EditOutlined, DeleteOutlined,
  AuditOutlined, UserOutlined, ArrowRightOutlined,
  CheckCircleFilled, MinusCircleOutlined, UsergroupAddOutlined,
} from "@ant-design/icons";
import { apiRequest } from "../../../utilities/apiMethod";
import { useThemeStore } from "../../../store/useThemeStore";

/* ── Avatar helpers ─────────────────────────────────────────────────────────── */
const PALETTE = ['#6366f1','#2563eb','#7c3aed','#e11d48','#0d9488','#ea580c','#059669','#d97706','#0891b2'];
function avatarColor(str) {
  if (!str) return '#6b7280';
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return PALETTE[Math.abs(h) % PALETTE.length];
}
function getInitials(name) {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length > 1 ? (p[0][0] + p[p.length - 1][0]).toUpperCase() : name.slice(0, 2).toUpperCase();
}
function hexToRgb(hex) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `${r},${g},${b}`;
}

/* ── Single approver card in the pipeline ───────────────────────────────────── */
function ApproverCard({ item, seq, user, isLast, onEdit, onRemove, themeColor }) {
  const name  = user?.Name || user?.UserName || `User #${item.UserLoginId}`;
  const email = user?.Email ?? "";
  const color = avatarColor(name);
  const rgb   = hexToRgb(themeColor);

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
      {/* Card */}
      <div style={{
        width: 190, borderRadius: 14, overflow: "hidden",
        border: `1.5px solid rgba(${rgb},0.18)`,
        boxShadow: `0 2px 12px rgba(${rgb},0.10)`,
        background: "#fff", transition: "box-shadow 0.2s",
        position: "relative",
      }}
        onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 6px 20px rgba(${rgb},0.18)`; }}
        onMouseLeave={e => { e.currentTarget.style.boxShadow = `0 2px 12px rgba(${rgb},0.10)`; }}
      >
        {/* Coloured top bar */}
        <div style={{ height: 4, background: `linear-gradient(90deg, ${themeColor}, ${color})` }} />

        {/* Sequence badge */}
        <div style={{
          position: "absolute", top: 12, right: 12,
          width: 22, height: 22, borderRadius: "50%",
          background: themeColor, display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 10, fontWeight: 900, color: "#fff",
          boxShadow: `0 2px 6px ${themeColor}55`,
        }}>
          {seq}
        </div>

        <div style={{ padding: "12px 12px 10px" }}>
          {/* Avatar + name */}
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 8 }}>
            <div style={{
              width: 36, height: 36, borderRadius: 10, flexShrink: 0,
              background: `linear-gradient(135deg, ${color}dd, ${color})`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 2px 6px ${color}44`,
            }}>
              <span style={{ fontSize: 13, fontWeight: 800, color: "#fff" }}>{getInitials(name)}</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</p>
              {email && <p style={{ margin: 0, fontSize: 10, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{email}</p>}
            </div>
          </div>

          {/* Badges */}
          <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 10 }}>
            {item.IsMandatory
              ? <span style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", background: "#f0fdf4", border: "1px solid #bbf7d0", padding: "1px 8px", borderRadius: 20 }}>Mandatory</span>
              : <span style={{ fontSize: 10, fontWeight: 600, color: "#9ca3af", background: "#f9fafb", border: "1px solid #f0f1f3", padding: "1px 8px", borderRadius: 20 }}>Optional</span>
            }
            {item.IsDeleted && (
              <span style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", background: "#fff1f2", border: "1px solid #fecaca", padding: "1px 8px", borderRadius: 20 }}>Removed</span>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 6 }}>
            <Tooltip title="Edit">
              <button onClick={onEdit} style={{
                flex: 1, height: 26, borderRadius: 7, border: `1px solid rgba(${rgb},0.25)`,
                background: `rgba(${rgb},0.06)`, cursor: "pointer", color: themeColor,
                fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
                transition: "all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = `rgba(${rgb},0.14)`; }}
                onMouseLeave={e => { e.currentTarget.style.background = `rgba(${rgb},0.06)`; }}
              >
                <EditOutlined style={{ fontSize: 11 }} /> Edit
              </button>
            </Tooltip>
            <Tooltip title="Remove">
              <button onClick={onRemove} style={{
                width: 26, height: 26, borderRadius: 7, border: "1px solid #fecaca",
                background: "#fff1f2", cursor: "pointer", color: "#ef4444",
                display: "flex", alignItems: "center", justifyContent: "center",
                transition: "all 0.15s",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff1f2"; }}
              >
                <DeleteOutlined style={{ fontSize: 11 }} />
              </button>
            </Tooltip>
          </div>
        </div>
      </div>

      {/* Arrow connector */}
      {!isLast && (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, padding: "0 6px" }}>
          <div style={{ width: 24, height: 2, background: `rgba(${rgb},0.30)`, borderRadius: 1 }} />
          <div style={{
            width: 0, height: 0,
            borderTop: "5px solid transparent",
            borderBottom: "5px solid transparent",
            borderLeft: `6px solid rgba(${rgb},0.40)`,
          }} />
        </div>
      )}
    </div>
  );
}

/* ── Main component ─────────────────────────────────────────────────────────── */
const ApprovalGroupUserMapping = () => {
  const { themeColor } = useThemeStore();
  const rgb = hexToRgb(themeColor);

  const [messageApi, contextHolder] = message.useMessage();

  const [approvalGroups,   setApprovalGroups]   = useState([]);
  const [users,            setUsers]            = useState([]);
  const [selectedGroupId,  setSelectedGroupId]  = useState(null);
  const [gridData,         setGridData]         = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [saving,           setSaving]           = useState(false);

  const [modalOpen,  setModalOpen]  = useState(false);
  const [editIndex,  setEditIndex]  = useState(null);
  const [form]                      = Form.useForm();

  useEffect(() => {
    fetchApprovalGroups();
    fetchUsers();
  }, []);

  useEffect(() => {
    if (selectedGroupId) fetchGroupUserMapping();
  }, [selectedGroupId]);

  async function fetchApprovalGroups() {
    try {
      const res = await apiRequest("GET", "ApprovalGroup");
      if (res?.IsSuccess) setApprovalGroups(res.Data ?? []);
    } catch { setApprovalGroups([]); }
  }

  async function fetchUsers() {
    try {
      const res = await apiRequest("GET", "Users/onlyapprover");
      if (res?.IsSuccess) setUsers(res.Data ?? []);
    } catch { setUsers([]); }
  }

  async function fetchGroupUserMapping() {
    setLoading(true);
    try {
      const res = await apiRequest("GET", `ApprovalGroupUserMapping/getApprovalGroupId/${selectedGroupId}`);
      if (res?.IsSuccess && res.Data) {
        const sorted = [...res.Data]
          .map((item, i) => ({ ...item, UserLoginId: item.UserId ?? item.UserLoginId, serialNo: i + 1 }))
          .sort((a, b) => (a.ApprovalSeq || 0) - (b.ApprovalSeq || 0));
        setGridData(sorted);
      } else { setGridData([]); }
    } catch { setGridData([]); }
    finally  { setLoading(false); }
  }

  /* ── Derived ────────────────────────────────────────────────────────────── */
  const selectedGroup = useMemo(() => approvalGroups.find(g => g.ID === selectedGroupId), [approvalGroups, selectedGroupId]);

  const sortedChain = useMemo(() =>
    [...gridData].filter(d => !d.IsDeleted).sort((a, b) => (a.ApprovalSeq || 0) - (b.ApprovalSeq || 0)),
    [gridData]
  );

  const removedItems = useMemo(() => gridData.filter(d => d.IsDeleted), [gridData]);

  function getUserInfo(id) { return users.find(u => u.UserLoginId === id || u.ID === id); }

  /* ── Available users (exclude already mapped, unless editing that slot) ── */
  const availableUsers = useMemo(() => {
    const usedIds = gridData
      .filter((_, i) => i !== editIndex)
      .filter(d => !d.IsDeleted)
      .map(d => d.UserLoginId);
    return users.filter(u => !usedIds.includes(u.UserLoginId));
  }, [users, gridData, editIndex]);

  /* ── Next suggested sequence ────────────────────────────────────────────── */
  const nextSeq = useMemo(() => {
    if (!gridData.length) return 1;
    return Math.max(...gridData.map(d => d.ApprovalSeq || 0)) + 1;
  }, [gridData]);

  /* ── Modal handlers ─────────────────────────────────────────────────────── */
  function openAdd() {
    setEditIndex(null);
    form.resetFields();
    form.setFieldsValue({ approvalSeq: nextSeq, isMandatory: true, isDeleted: false });
    setModalOpen(true);
  }

  function openEdit(idx) {
    const item = gridData[idx];
    setEditIndex(idx);
    form.setFieldsValue({
      UserLoginId: item.UserLoginId,
      approvalSeq: item.ApprovalSeq,
      isMandatory: item.IsMandatory ?? false,
      isDeleted:   item.IsDeleted   ?? false,
    });
    setModalOpen(true);
  }

  function handleRemove(idx) {
    setGridData(prev => {
      const updated = [...prev];
      updated[idx] = { ...updated[idx], IsDeleted: true };
      return updated;
    });
    messageApi.success("Approver removed from chain");
  }

  function onModalFinish(values) {
    const newItem = {
      UserLoginId: values.UserLoginId,
      ApprovalSeq: parseInt(values.approvalSeq),
      IsMandatory: !!values.isMandatory,
      IsDeleted:   !!values.isDeleted,
    };

    // Duplicate checks (excluding current edit slot)
    const others = gridData.filter((_, i) => i !== editIndex).filter(d => !d.IsDeleted);
    if (others.some(d => d.UserLoginId === newItem.UserLoginId)) {
      messageApi.error("This user is already in the approval chain"); return;
    }
    if (others.some(d => d.ApprovalSeq === newItem.ApprovalSeq)) {
      messageApi.error("This sequence number is already used"); return;
    }

    setGridData(prev => {
      const updated = [...prev];
      if (editIndex !== null) {
        updated[editIndex] = { ...updated[editIndex], ...newItem };
      } else {
        updated.push({ ...newItem, serialNo: updated.length + 1 });
      }
      return updated;
    });

    setModalOpen(false);
    messageApi.success(editIndex !== null ? "Approver updated" : "Approver added to chain");
  }

  /* ── Save mapping ───────────────────────────────────────────────────────── */
  async function handleSave() {
    if (!selectedGroupId) { messageApi.error("Select an approval group first"); return; }
    if (!sortedChain.length) { messageApi.error("Add at least one approver"); return; }
    setSaving(true);
    try {
      const payload = {
        approvalGroupId: selectedGroupId,
        users: gridData.map(item => ({
          userId:      item.UserLoginId,
          approvalSeq: item.ApprovalSeq,
          isMandatory: item.IsMandatory,
          isDeleted:   item.IsDeleted,
        })),
      };
      const res = await apiRequest("POST", "ApprovalGroupUserMapping", payload);
      if (res?.IsSuccess) {
        messageApi.success(res.displayMessage || "Mapping saved successfully");
        fetchGroupUserMapping();
      } else {
        throw new Error(res?.displayMessage || "Failed to save mapping");
      }
    } catch (e) { messageApi.error(e.message || "Failed to save mapping"); }
    finally     { setSaving(false); }
  }

  /* ── Render ─────────────────────────────────────────────────────────────── */
  return (
    <div style={{ padding: "4px 0", display: "flex", flexDirection: "column", gap: 10 }}>
      {contextHolder}

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 14, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "12px 18px",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{
            width: 38, height: 38, borderRadius: 10, flexShrink: 0, background: themeColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 14px ${themeColor}44`, transition: "background 0.3s",
          }}>
            <UsergroupAddOutlined style={{ fontSize: 17, color: "#fff" }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Approval User Mapping</h2>
            <p style={{ margin: 0, fontSize: 12, color: "#9ca3af" }}>Build the approval chain for each group</p>
          </div>
        </div>

        {selectedGroupId && (
          <Button
            icon={<SaveOutlined />} onClick={handleSave} loading={saving}
            disabled={!sortedChain.length}
            style={{
              height: 38, borderRadius: 9, fontWeight: 700, fontSize: 13,
              background: sortedChain.length ? themeColor : undefined,
              borderColor: sortedChain.length ? themeColor : undefined,
              color: sortedChain.length ? "#fff" : undefined,
              boxShadow: sortedChain.length ? `0 2px 8px ${themeColor}44` : undefined,
              transition: "all 0.3s",
            }}
          >
            Save Mapping
          </Button>
        )}
      </div>

      {/* ── Group selector card ──────────────────────────────────────────── */}
      <div style={{
        background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3",
        boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "14px 18px",
        display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
      }}>
        <div style={{ flex: "0 0 280px" }}>
          <label style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.07em", display: "block", marginBottom: 5 }}>
            Approval Group
          </label>
          <Select
            placeholder="Select an approval group…"
            value={selectedGroupId}
            onChange={val => { setSelectedGroupId(val); setGridData([]); }}
            options={approvalGroups.map(g => ({ value: g.ID, label: g.GroupName }))}
            allowClear showSearch optionFilterProp="label"
            style={{ width: "100%", height: 38 }}
          />
        </div>

        {selectedGroup && (
          <div style={{
            flex: 1, display: "flex", alignItems: "center", gap: 12,
            padding: "10px 16px", borderRadius: 10,
            background: `rgba(${rgb},0.06)`,
            border: `1px solid rgba(${rgb},0.20)`,
            minWidth: 0,
          }}>
            <div style={{ width: 34, height: 34, borderRadius: 10, background: themeColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <AuditOutlined style={{ color: "#fff", fontSize: 15 }} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <p style={{ margin: 0, fontWeight: 700, fontSize: 13, color: "#111827" }}>{selectedGroup.GroupName}</p>
              <div style={{ display: "flex", gap: 8, marginTop: 2, flexWrap: "wrap" }}>
                {selectedGroup.VendorType && (
                  <span style={{ fontSize: 11, color: themeColor, fontWeight: 600 }}>Type: {selectedGroup.VendorType}</span>
                )}
                {selectedGroup.Description && (
                  <span style={{ fontSize: 11, color: "#9ca3af" }}>{selectedGroup.Description}</span>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
              <div style={{ textAlign: "center" }}>
                <p style={{ margin: 0, fontSize: 20, fontWeight: 900, color: themeColor, lineHeight: 1 }}>{sortedChain.length}</p>
                <p style={{ margin: 0, fontSize: 10, color: "#9ca3af" }}>Approvers</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ── No group state ───────────────────────────────────────────────── */}
      {!selectedGroupId && (
        <div style={{
          background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3",
          boxShadow: "0 1px 6px rgba(0,0,0,0.04)", padding: "56px 24px",
          display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
        }}>
          <div style={{ width: 56, height: 56, borderRadius: 16, background: `rgba(${rgb},0.08)`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <UsergroupAddOutlined style={{ fontSize: 26, color: themeColor }} />
          </div>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: "#111827" }}>Select an Approval Group</p>
          <p style={{ margin: 0, fontSize: 13, color: "#9ca3af", textAlign: "center", maxWidth: 340 }}>
            Choose a group above to view and configure its approval chain.
          </p>
        </div>
      )}

      {/* ── Loading ──────────────────────────────────────────────────────── */}
      {selectedGroupId && loading && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", padding: "56px", display: "flex", justifyContent: "center", alignItems: "center", gap: 12 }}>
          <Spin size="large" />
          <span style={{ fontSize: 13, color: "#9ca3af" }}>Loading approval chain…</span>
        </div>
      )}

      {/* ── Pipeline ─────────────────────────────────────────────────────── */}
      {selectedGroupId && !loading && (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #f0f1f3", boxShadow: "0 1px 6px rgba(0,0,0,0.04)", overflow: "hidden" }}>

          {/* Pipeline header */}
          <div style={{ padding: "12px 16px", borderBottom: "1px solid #f0f1f3", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <p style={{ margin: 0, fontSize: 13, fontWeight: 700, color: "#111827" }}>Approval Chain</p>
              <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>
                {sortedChain.length
                  ? `${sortedChain.length} approver${sortedChain.length > 1 ? "s" : ""} · processed in sequence order`
                  : "No approvers configured yet"}
              </p>
            </div>
            <Button
              icon={<PlusOutlined />} onClick={openAdd}
              style={{ height: 34, borderRadius: 8, fontWeight: 700, fontSize: 12, background: themeColor, borderColor: themeColor, color: "#fff", boxShadow: `0 2px 8px ${themeColor}44` }}
            >
              Add Approver
            </Button>
          </div>

          {/* Pipeline body */}
          <div style={{ padding: "20px 18px", minHeight: 160 }}>
            {sortedChain.length === 0 ? (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "30px 0", gap: 10 }}>
                <div style={{ width: 48, height: 48, borderRadius: 12, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <UserOutlined style={{ fontSize: 22, color: "#d1d5db" }} />
                </div>
                <p style={{ margin: 0, fontSize: 14, fontWeight: 600, color: "#9ca3af" }}>No approvers in chain</p>
                <p style={{ margin: 0, fontSize: 12, color: "#c4c7cd" }}>Click "Add Approver" to build the chain</p>
              </div>
            ) : (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, alignItems: "center" }}>
                {sortedChain.map((item, i) => {
                  const originalIdx = gridData.findIndex(d => d === item);
                  return (
                    <ApproverCard
                      key={item.UserLoginId + "_" + item.ApprovalSeq}
                      item={item}
                      seq={item.ApprovalSeq}
                      user={getUserInfo(item.UserLoginId)}
                      isLast={i === sortedChain.length - 1}
                      onEdit={() => openEdit(originalIdx)}
                      onRemove={() => handleRemove(originalIdx)}
                      themeColor={themeColor}
                    />
                  );
                })}

                {/* Add next button at the end of the chain */}
                <div style={{ display: "flex", alignItems: "center", gap: 0 }}>
                  <div style={{ width: 24, height: 2, background: `rgba(${rgb},0.20)`, borderRadius: 1 }} />
                  <button onClick={openAdd} style={{
                    width: 42, height: 42, borderRadius: 12,
                    border: `2px dashed rgba(${rgb},0.35)`,
                    background: `rgba(${rgb},0.04)`,
                    cursor: "pointer", color: themeColor,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18, transition: "all 0.15s",
                  }}
                    onMouseEnter={e => { e.currentTarget.style.background = `rgba(${rgb},0.10)`; e.currentTarget.style.borderColor = themeColor; }}
                    onMouseLeave={e => { e.currentTarget.style.background = `rgba(${rgb},0.04)`; e.currentTarget.style.borderColor = `rgba(${rgb},0.35)`; }}
                  >
                    <PlusOutlined style={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Removed items section */}
          {removedItems.length > 0 && (
            <div style={{ borderTop: "1px dashed #f0f1f3", padding: "10px 18px 14px" }}>
              <p style={{ margin: "0 0 8px", fontSize: 11, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                Removed from chain
              </p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {removedItems.map((item, i) => {
                  const user = getUserInfo(item.UserLoginId);
                  const name = user?.Name || user?.UserName || `User #${item.UserLoginId}`;
                  return (
                    <span key={i} style={{
                      fontSize: 11, fontWeight: 600, color: "#9ca3af",
                      background: "#f9fafb", border: "1px solid #f0f1f3",
                      padding: "3px 10px", borderRadius: 20, textDecoration: "line-through",
                    }}>
                      {name}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Add / Edit modal ─────────────────────────────────────────────── */}
      <Modal
        open={modalOpen} onCancel={() => setModalOpen(false)} footer={null}
        width={440} centered destroyOnClose
        title={
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: "#f4f5f7", display: "flex", alignItems: "center", justifyContent: "center" }}>
              {editIndex !== null
                ? <EditOutlined style={{ fontSize: 13, color: themeColor }} />
                : <PlusOutlined style={{ fontSize: 13, color: themeColor }} />
              }
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 14, fontWeight: 700, color: "#111827", lineHeight: 1.2 }}>
                {editIndex !== null ? "Edit Approver" : "Add Approver"}
              </p>
              <p style={{ margin: "1px 0 0", fontSize: 11, color: "#9ca3af" }}>
                {editIndex !== null ? "Update approver details" : "Add a new approver to the chain"}
              </p>
            </div>
          </div>
        }
      >
        <Form form={form} layout="vertical" onFinish={onModalFinish} requiredMark={false} style={{ marginTop: 10 }}>

          <Form.Item
            name="UserLoginId" label={<span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Approver</span>}
            rules={[{ required: true, message: "Select an approver" }]}
            style={{ marginBottom: 12 }}
          >
            <Select
              placeholder="Select approver…" showSearch optionFilterProp="label"
              style={{ height: 40 }}
              options={availableUsers.map(u => ({
                value: u.UserLoginId,
                label: `${u.Name || u.UserName} ${u.Email ? `(${u.Email})` : ""}`,
              }))}
              optionRender={opt => {
                const user = availableUsers.find(u => u.UserLoginId === opt.value);
                if (!user) return opt.label;
                const name  = user.Name || user.UserName || "";
                const color = avatarColor(name);
                return (
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 7, background: `linear-gradient(135deg, ${color}cc, ${color})`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#fff" }}>{getInitials(name)}</span>
                    </div>
                    <div>
                      <p style={{ margin: 0, fontSize: 13, fontWeight: 600, color: "#111827" }}>{name}</p>
                      {user.Email && <p style={{ margin: 0, fontSize: 11, color: "#9ca3af" }}>{user.Email}</p>}
                    </div>
                  </div>
                );
              }}
            />
          </Form.Item>

          <Form.Item
            name="approvalSeq" label={<span style={{ fontSize: 10, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em" }}>Sequence Order</span>}
            rules={[{ required: true, message: "Required" }, { type: "number", min: 1, message: "Must be ≥ 1", transform: v => Number(v) }]}
            style={{ marginBottom: 12 }}
          >
            <Input type="number" min={1} placeholder="e.g. 1" style={{ borderRadius: 8, height: 38 }} />
          </Form.Item>

          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, border: "1px solid #f0f1f3", background: "#fafafa" }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#111827" }}>Mandatory</p>
                <p style={{ margin: 0, fontSize: 10, color: "#9ca3af" }}>Must approve to proceed</p>
              </div>
              <Form.Item name="isMandatory" valuePropName="checked" noStyle initialValue={true}>
                <Switch size="small" />
              </Form.Item>
            </div>
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", borderRadius: 8, border: "1px solid #f0f1f3", background: "#fafafa" }}>
              <div>
                <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: "#111827" }}>Removed</p>
                <p style={{ margin: 0, fontSize: 10, color: "#9ca3af" }}>Exclude from chain</p>
              </div>
              <Form.Item name="isDeleted" valuePropName="checked" noStyle initialValue={false}>
                <Switch size="small" />
              </Form.Item>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, paddingTop: 8, borderTop: "1px solid #f0f1f3" }}>
            <Button onClick={() => setModalOpen(false)} style={{ flex: 1, height: 38, borderRadius: 8, fontWeight: 600 }}>Cancel</Button>
            <Button htmlType="submit" style={{ flex: 2, height: 38, borderRadius: 8, fontWeight: 700, background: themeColor, borderColor: themeColor, color: "#fff", transition: "background 0.3s" }}>
              {editIndex !== null ? "Save Changes" : "Add to Chain"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default ApprovalGroupUserMapping;
